import { getAuthHeaders } from '../lib/auth-headers';
/**
 * Страница деталей образовательной коллекции
 * apps/web/src/pages/CollectionDetailPage.tsx
 */

import { useEffect, useState } from 'react';
import CourseDiagnostic from '../components/CourseDiagnostic';
import CourseChannels from '../components/CourseChannels';
import { shareResult } from "../lib/share";
import { detectPlatform, isCoursesSite } from "../platform/detect";
import { useParams, useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../stores/collections';
import type { Section, Module, UserCollectionProgress } from '@beshek/shared';
import { coursesBotUrl } from '../lib/bots';

// ==================== Helpers ====================

type ModuleStatus = 'locked' | 'available' | 'started' | 'completed';

function getModuleStatus(
  module: Module,
  moduleIndex: number,
  section: Section,
  sectionIndex: number,
  progress: UserCollectionProgress | null,
  hasAccess: boolean,
  previewSections: number,
  allSections: Section[],
  sequential: boolean
): ModuleStatus {
  if (!hasAccess && sectionIndex >= previewSections) {
    return 'locked';
  }

  const moduleProgress = progress?.moduleProgress[module.id];
  
  if (moduleProgress?.completed) return 'completed';
  if (moduleProgress?.started) return 'started';
  
  if (sectionIndex === 0 && moduleIndex === 0) return 'available';
  
  // Курс без строгого порядка: открыт весь, человек сам выбирает точку входа.
  // (Раньше здесь открывался только первый модуль раздела, и флаг фактически не работал.)
  if (!sequential) return 'available';
  
  if (sequential && moduleIndex === 0 && sectionIndex > 0) {
    const prevSection = allSections[sectionIndex - 1];
    const allPrevCompleted = prevSection.modules.every((m: Module) => 
      progress?.moduleProgress[m.id]?.completed
    );
    return allPrevCompleted ? 'available' : 'locked';
  }
  
  const prevModule = section.modules[moduleIndex - 1];
  const prevProgress = progress?.moduleProgress[prevModule.id];
  
  if (prevProgress?.completed) return 'available';
  
  return 'locked';
}

/** «1 тема», «4 темы», «27 тем» — иначе карточка выглядит машинной. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/** «~40 мин» или «~6,5 ч» — часы читаются лучше, чем 390 минут. */
function readableTime(minutes: number): string {
  if (!minutes) return '';
  if (minutes < 90) return `~${minutes} мин`;
  const hours = Math.round(minutes / 6) / 10;
  return `~${String(hours).replace('.', ',')} ч`;
}

const DIFFICULTY: Record<string, string> = {
  beginner: '🌱 Начальный',
  intermediate: '🌿 Средний',
  advanced: '🌳 Продвинутый',
};

// ==================== Components ====================

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span>Назад</span>
    </button>
  );
}

function ProgressBar({ percent, xp }: { percent: number; xp: number }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600">Прогресс</span>
        <span className="text-sm font-medium text-blue-600">{percent}%</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="text-sm text-gray-500">
        ⚡ +{xp} XP заработано
      </div>
    </div>
  );
}

function ModuleCard({
  module,
  status,
  bestScore,
  recommended,
  onStart,
}: {
  module: Module;
  status: ModuleStatus;
  bestScore?: number;
  recommended?: boolean;
  onStart: () => void;
}) {
  const statusConfig = {
    locked: {
      icon: '🔒',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-400',
      action: null,
    },
    available: {
      icon: '▶️',
      bg: 'bg-white',
      border: 'border-gray-200 hover:border-blue-300',
      text: 'text-gray-900',
      action: 'Начать',
    },
    started: {
      icon: '⏳',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-900',
      action: 'Продолжить',
    },
    completed: {
      icon: '✅',
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-900',
      action: 'Пройти снова',
    },
  };

  const config = statusConfig[status];
  const isClickable = status !== 'locked';

  return (
    <button
      onClick={isClickable ? onStart : undefined}
      disabled={!isClickable}
      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all
                  ${config.bg} ${config.border} ${!isClickable ? 'cursor-not-allowed' : ''}`}
    >
      <span className="text-2xl">{config.icon}</span>
      
      <div className="flex-1 text-left">
        <h4 className={`font-medium ${config.text}`}>{module.name}</h4>
        {module.description && (
          <p className="text-sm text-gray-500 mt-0.5">{module.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {recommended && status !== 'completed' && (
          <span className="rounded-full bg-[#E5F2EF] px-2 py-0.5 text-xs font-medium text-[#0F766E]">рекомендовано</span>
        )}
        {bestScore !== undefined && status === 'completed' && (
          <span className="text-sm font-medium text-green-600">{bestScore}%</span>
        )}
        {config.action && (
          <span className="text-blue-500">→</span>
        )}
      </div>
    </button>
  );
}

function SectionCard({
  section,
  sectionIndex,
  progress,
  hasAccess,
  previewSections,
  allSections,
  sequential,
  recommended,
  onModuleStart,
}: {
  section: Section;
  sectionIndex: number;
  progress: UserCollectionProgress | null;
  hasAccess: boolean;
  previewSections: number;
  allSections: Section[];
  sequential: boolean;
  recommended?: string[];
  onModuleStart: (moduleId: string) => void;
}) {
  const isLocked = !hasAccess && sectionIndex >= previewSections;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden
                     ${isLocked ? 'opacity-75' : ''}`}>
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">{section.name}</h3>
        {section.description && (
          <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
        )}
        {isLocked && (
          <p className="text-sm text-amber-600 mt-1">
            🔒 Требуется покупка
          </p>
        )}
      </div>
      
      <div className="p-3 space-y-2">
        {section.modules.map((module: Module, moduleIndex: number) => {
          const status = getModuleStatus(
            module,
            moduleIndex,
            section,
            sectionIndex,
            progress,
            hasAccess,
            previewSections,
            allSections,
            sequential
          );
          const moduleProgress = progress?.moduleProgress[module.id];
          
          return (
            <ModuleCard
              key={module.id}
              module={module}
              status={status}
              bestScore={moduleProgress?.bestScore}
              recommended={recommended?.includes(module.id)}
              onStart={() => onModuleStart(module.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PurchaseButton({
  price,
  onPurchase,
  busy,
}: {
  price: number;
  onPurchase: () => void;
  busy?: boolean;
}) {
  // На сайте курсов платят рублями через ЮKassa; в Telegram/VK — как раньше (звёзды/голоса).
  const site = isCoursesSite();
  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 safe-area-pb">
      <button
        onClick={onPurchase}
        disabled={busy}
        className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl shadow-lg disabled:opacity-60 hover:from-amber-600 hover:to-orange-600 transition-all"
      >
        {busy ? 'Открываем оплату…' : site ? `Купить за ${price} ₽` : `⭐ Разблокировать за ${price} Stars`}
      </button>
      {site && (
        <p className="mt-2 text-center text-[11px] text-gray-400">
          Картой или СБП на странице ЮKassa · <a href="/refund" className="underline">возврат</a> · <a href="/terms" className="underline">условия</a>
        </p>
      )}
    </div>
  );
}

function EmbedModal({ embedCode, onClose }: { embedCode: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [size, setSize] = useState<'compact' | 'medium' | 'full'>('medium');

  const sizes = {
    compact: { width: '600', height: '500', label: 'Компактный (600×500)' },
    medium: { width: '800', height: '700', label: 'Средний (800×700)' },
    full: { width: '100%', height: '700', label: 'На всю ширину' },
  };

  const currentSize = sizes[size];
  const code = `<iframe src="${embedCode}" width="${currentSize.width}" height="${currentSize.height}" frameborder="0" style="border-radius:16px;border:1px solid #e5e7eb;max-width:800px" allow="clipboard-write"></iframe>`;

  const handleCopy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Встроить на сайт</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Размер</label>
            <div className="flex gap-2">
              {(Object.keys(sizes) as Array<keyof typeof sizes>).map(key => (
                <button
                  key={key}
                  onClick={() => setSize(key)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${size === key ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'}`}
                >
                  {sizes[key].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">HTML-код</label>
            <div className="relative">
              <pre className="bg-gray-900 text-green-400 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
            </div>
          </div>

          <button
            onClick={handleCopy}
            className={`w-full py-3 rounded-xl font-semibold transition-all ${copied ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {copied ? '✅ Скопировано!' : '📋 Копировать код'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            Вставьте этот код на свой сайт — курс откроется в iframe
          </p>
        </div>
      </div>
    </div>
  );
}

function NotFoundState({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="text-6xl mb-4">🧭</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Такого курса нет
        </h3>
        <p className="text-gray-500 mb-6">
          Ссылка ведёт в никуда — возможно, курс переименовали.
          Посмотрите, что есть сейчас.
        </p>
        <button
          onClick={onBack}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
        >
          Все курсы
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4" />
        <div className="h-32 bg-gray-200 rounded-2xl" />
        <div className="h-48 bg-gray-200 rounded-2xl" />
        <div className="h-48 bg-gray-200 rounded-2xl" />
      </div>
    </div>
  );
}

// ==================== Main Page ====================

export default function CollectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEmbed, setShowEmbed] = useState(false);
  const [hasDiagnostic, setHasDiagnostic] = useState(false);
  const [recommended, setRecommended] = useState<string[]>([]); // темы из разбора «с чего начать»
  
  const {
    currentCollection,
    currentProgress,
    hasAccess,
    isLoading,
    error,
    loadCollectionDetail,
    startModule,
  } = useCollectionsStore();

  useEffect(() => {
    if (id) {
      loadCollectionDetail(id);
    }
  }, [id]);

  const handleBack = () => {
    navigate('/collections');
  };

  const handleModuleStart = async (moduleId: string) => {
    if (!id) return;
    
    const result = await startModule(id, moduleId);
    
    if (result?.hasLesson && result?.lessonId) {
      navigate(`/collections/${id}/module/${moduleId}`);
    } else {
      navigate(`/collections/${id}/module/${moduleId}`);
    }
  };

  const [payBusy, setPayBusy] = useState(false);
  const handlePurchase = async () => {
    if (!isCoursesSite()) {
      alert('Оплата курсов в Telegram пока не подключена. Купить можно на сайте kurs.mugstel.ru');
      return;
    }
    setPayBusy(true);
    try {
      const r = await fetch('/api/pay/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ project: 'courses', itemId: id }),
      });
      const d = await r.json();
      if (!r.ok || !d.confirmationUrl) throw new Error(d.error || 'Не удалось создать платёж');
      window.location.href = d.confirmationUrl;
    } catch (e: any) {
      alert(e?.message || 'Не удалось открыть оплату');
      setPayBusy(false);
    }
  };
  const handleShare = () => {
    const platform = detectPlatform();
    if (!currentCollection) return;
    let url: string;
    if (platform === "vk") {
      url = "https://vk.com/app52236695#collection=" + id;
    } else if (platform === "telegram") {
      url = coursesBotUrl("collection_" + id);
    } else {
      // Витрин несколько (игры и курсы) — делимся тем адресом, где человек сейчас.
      // На курсах ведём на /course/: эту страницу отдаёт сервер, у неё есть og-теги,
      // и в мессенджере превью показывает название курса. Оболочка приложения
      // общая на две витрины, её заголовок — «Beshek — Party Games», поэтому
      // ссылка на /collections/ выдавала курс за игры (31.08.2026).
      // На игровом домене оставляем как было: у игр страниц /course/ нет.
      url = isCoursesSite()
        ? window.location.origin + "/course/" + id
        : window.location.origin + "/collections/" + id;
    }
    const text = currentCollection.icon + " " + currentCollection.name + " — образовательный курс";
    shareResult({ text, url });
  };
  if (isLoading) {
    return <LoadingState />;
  }
  // Загрузка кончилась, а подборки нет — значит адрес неверный. Раньше здесь
  // крутился вечный загрузчик: человек не понимал, сломался сайт или грузится.
  // Ловилось на ссылке с незаполненным шаблоном «/collections/название» —
  // 7 заходов за три месяца, все в пустоту (31.08.2026).
  if (!currentCollection) {
    return <NotFoundState onBack={() => navigate('/collections')} />;
  }

  const collection = currentCollection;
  const previewSections = collection.access.previewSections ?? 1;
  const completionPercent = currentProgress
    ? Math.round(
        (Object.values(currentProgress.moduleProgress).filter((m: any) => m.completed).length /
          (collection.stats.totalModules || 1)) *
          100
      )
    : 0;

  // Куда вести по главной кнопке: сначала начатое и незаконченное, иначе первое
  // доступное. Если всё пройдено — кнопки нет, курс закончен.
  const nextModule = (() => {
    let firstAvailable: Module | null = null;
    // Тема, которую посоветовал разбор «с чего начать», идёт первой, если доступна и не пройдена
    for (const rid of recommended) {
      for (let si = 0; si < collection.sections.length; si++) {
        const section = collection.sections[si];
        const mi = section.modules.findIndex((m: Module) => m.id === rid);
        if (mi < 0) continue;
        const st = getModuleStatus(
          section.modules[mi], mi, section, si, currentProgress, hasAccess,
          previewSections, collection.sections, collection.sequential !== false,
        );
        if (st === 'available' || st === 'started') return section.modules[mi];
      }
    }
    for (let si = 0; si < collection.sections.length; si++) {
      const section = collection.sections[si];
      for (let mi = 0; mi < section.modules.length; mi++) {
        const module = section.modules[mi];
        const status = getModuleStatus(
          module, mi, section, si, currentProgress, hasAccess,
          previewSections, collection.sections, collection.sequential !== false,
        );
        if (status === 'started') return module;
        if (status === 'available' && !firstAvailable) firstAvailable = module;
      }
    }
    return firstAvailable;
  })();

  const embedUrl = `${window.location.origin}/embed/collection/${id}`;

  return (
    <div className={`min-h-screen bg-gray-50 ${!hasAccess ? 'pb-24' : ''}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="flex items-center justify-between">
          <BackButton onClick={handleBack} />
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowEmbed(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Встроить на сайт"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </button>
            <button
              onClick={handleShare}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Поделиться"
            >
              📤
            </button>
          </div>
        </div>
        
        {/* Обложка курса. Пока её нет — крупный значок на мягкой заливке:
            честнее пустого прямоугольника или чужой фотографии. */}
        {collection.cover ? (
          <img
            src={collection.cover}
            alt={collection.name}
            /* 17.09.2026, владелец: «открываешь курс — фотография на весь экран,
               бесполезная». Тот же размер, что у статей: высота до 220 точек, по центру,
               без обрезки — раньше было w-full + object-cover, то есть во всю ширину. */
            className="mt-4 max-h-[220px] w-auto mx-auto rounded-2xl object-contain"
          />
        ) : (
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 py-8 text-center text-6xl">
            {collection.icon}
          </div>
        )}

        <h1 className="mt-4 text-xl font-bold text-gray-900">{collection.name}</h1>
        <p className="mt-1 text-sm text-gray-600">{collection.description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
          {DIFFICULTY[collection.difficulty] && <span>{DIFFICULTY[collection.difficulty]}</span>}
          {collection.stats.totalModules ? (
            <>
              <span>·</span>
              <span>{plural(collection.stats.totalModules, 'тема', 'темы', 'тем')}</span>
            </>
          ) : null}
          {collection.stats.estimatedMinutes ? (
            <>
              <span>·</span>
              <span>{readableTime(collection.stats.estimatedMinutes)}</span>
            </>
          ) : null}
          <span>·</span>
          {collection.access.type === 'free' ? (
            <span className="text-green-600">Бесплатно</span>
          ) : (
            <span className="text-amber-600">
              {collection.access.price ? `${collection.access.price} Stars` : 'Платный'}
            </span>
          )}
        </div>

        {/* Одна главная кнопка: ведёт туда, где человек остановился, а не «в курс вообще» */}
        {nextModule && (
          <button
            onClick={() => handleModuleStart(nextModule.id)}
            className="mt-4 w-full rounded-xl bg-blue-500 py-3 font-medium text-white
                       transition-colors hover:bg-blue-600"
          >
            {currentProgress ? '▶️ Продолжить' : '▶️ Начать курс'}
          </button>
        )}

        {/* Кто не знает, с чего начать, — тому разбор; он ниже, просто доводим до него */}
        {!currentProgress && hasAccess && hasDiagnostic && (
          <button
            onClick={() =>
              document.getElementById('diagnostic')?.scrollIntoView({ behavior: 'smooth' })
            }
            className="mt-2 w-full text-sm text-blue-600 hover:underline"
          >
            Не знаете, с чего начать? Ответьте на несколько вопросов
          </button>
        )}

        {/* Про сертификат говорим на входе, а не в конце: обещание документа —
            одна из причин, по которым курс вообще дочитывают до финиша. */}
        <p className="mt-3 text-sm text-gray-600">
          {currentProgress?.completed
            ? '🎓 Курс пройден — сертификат в вашем профиле.'
            : `🎓 Пройдёте все темы — получите сертификат с вашим результатом и номером.`}
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Progress */}
        {currentProgress && (
          <ProgressBar percent={completionPercent} xp={currentProgress.xpEarned} />
        )}

        {/* Разбор «с чего начать» — только тем, кто ещё не начинал курс */}
        {hasAccess && (
          <div id="diagnostic">
          <CourseDiagnostic
            collectionId={id!}
            sections={collection.sections}
            onModuleStart={handleModuleStart}
            started={!!currentProgress}
            onAvailability={setHasDiagnostic}
            onResult={(mods) => setRecommended(mods ?? [])}
          />
          </div>
        )}

        {/* Карта навыков: раздел = навык, процент = пройденные темы раздела (формат v2) */}
        {currentProgress && collection.sections.length > 1 && (() => {
          const rows = collection.sections.map((sec: Section) => {
            const total = sec.modules.length;
            const done = sec.modules.filter((m: Module) => currentProgress.moduleProgress[m.id]?.completed).length;
            return { id: sec.id, name: sec.name, pct: total ? Math.round((done / total) * 100) : 0 };
          });
          const last = rows[rows.length - 1];
          const readyForFinal = rows.slice(0, -1).every((r) => r.pct === 100) && last.pct < 100;
          return (
            <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold text-gray-900">Карта навыков</h3>
              {rows.map((r) => (
                <div key={r.id} className="mb-2">
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="text-gray-700">{r.name}</span>
                    <span className={r.pct === 100 ? 'font-medium text-[#0F766E]' : 'text-gray-500'}>{r.pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100">
                    <div className="h-1.5 rounded-full bg-[#0F766E] transition-all" style={{ width: `${r.pct}%` }} />
                  </div>
                </div>
              ))}
              {readyForFinal && (
                <p className="mt-2 text-sm text-[#0F766E]">Готовы к итоговой проверке: осталась часть «{last.name}»</p>
              )}
            </div>
          );
        })()}

        {/* Sections */}
        {collection.sections.map((section: Section, sectionIndex: number) => (
          <SectionCard
            key={section.id}
            section={section}
            sectionIndex={sectionIndex}
            progress={currentProgress}
            hasAccess={hasAccess}
            previewSections={previewSections}
            allSections={collection.sections}
            sequential={collection.sequential !== false}
            recommended={recommended}
            onModuleStart={handleModuleStart}
          />
        ))}

        {/* Наши сообщества по теме — внизу, когда человек уже посмотрел содержание */}
        <CourseChannels collectionId={id!} />
      </div>

      {/* Purchase Button */}
      {!hasAccess && collection.access.price && (
        <PurchaseButton price={collection.access.price} onPurchase={handlePurchase} busy={payBusy} />
      )}

      {/* Embed Modal */}
      {showEmbed && (
        <EmbedModal embedCode={embedUrl} onClose={() => setShowEmbed(false)} />
      )}
    </div>
  );
}